import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import { FiArrowRight, FiEye, FiEyeOff, FiUnlock } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { confirmPasswordReset } from "@/features/auth/api";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  password: Yup.string().min(8, "Min 8 characters").required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

interface ResetValues {
  password: string;
  confirmPassword: string;
}

/**
 * Set-a-new-password form. The single-use token comes from the emailed link's
 * `?token=` query param. On success the backend has revoked every session (it
 * does not log the user in), so we send them to /login to sign in fresh.
 */
export function ResetPasswordForm() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(values: ResetValues, { setErrors }: FormikHelpers<ResetValues>) {
    try {
      await confirmPasswordReset(token, values.password);
      toast.success("Password reset", { description: "Sign in with your new password." });
      navigate("/login", { replace: true });
    } catch (err) {
      // Map a server-side field error (rejected password) to the field, or bind a
      // bad/expired token to the password field so the message is visible.
      toast.error(
        applyApiErrors(
          err,
          { setErrors },
          {
            fallback: "Couldn't reset your password. The link may have expired.",
            fieldMap: { token: "password", new_password: "password" },
          },
        ),
      );
    }
  }

  if (!token) {
    return (
      <Card className="gap-0 p-6 sm:p-10">
        <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">Reset link</h1>
        <p className="text-muted-foreground">
          This reset link is missing its token. Please open the most recent link from your
          password-reset email, or request a new one.
        </p>
        <p className="text-muted-foreground mt-6 text-sm">
          <Link to="/forgot-password" className="text-primary hover:text-primary/80 font-medium">
            Request a new link
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="gap-0 p-6 sm:p-10">
        <div className="mb-10">
          <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">Set a new password</h1>
          <p className="text-muted-foreground">Choose a strong password you don&apos;t use elsewhere.</p>
        </div>

        <Formik<ResetValues>
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-6">
              <FormField
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••"
                icon={<FiUnlock className="size-4" />}
                className="h-11"
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEye className="size-4" /> : <FiEyeOff className="size-4" />}
                  </button>
                }
              />
              <FormField
                name="confirmPassword"
                label="Confirm New Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••"
                icon={<FiUnlock className="size-4" />}
                className="h-11"
              />
              <Button type="submit" disabled={isSubmitting} className="h-11 gap-2">
                {isSubmitting ? "Resetting..." : "Reset password"}
                <FiArrowRight className="size-4" />
              </Button>
            </Form>
          )}
        </Formik>
      </Card>

      <p className="text-muted-foreground mt-8 text-center text-sm">
        <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
          Back to sign in
        </Link>
      </p>
    </>
  );
}
